import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { SessionNotice } from "../../components/SessionNotice";
import { radioKeys } from "./radioKeys";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import type { useFlowState } from "../../features/booking/useFlowState";
import { StepLeadBar, StepFoot, StepMeta } from "./StepChrome";
import { FlowFrame, requestStepFocus } from "../../components/PageShell";
import type { ReactNode } from "react";
import { Skeleton } from "../../../../../shared/ui/Working";
import Footer from "../../components/Footer";

interface Props {
  verification?: boolean;
  /**
   * Offer "Sign in" beside Continue (v3 landing: Continue + Sign in). The only
   * way into Book sign-in, and so into Apple / Google, for a professional who
   * accepts guests. Book offers it to a guest while no booking is unresolved.
   */
  signIn?: boolean;
  slug: string;
  profile: PublicProfessionalProfile;
  flow: ReturnType<typeof useFlowState>;
}

// Step 1 of 4 — what are you booking?
//
// Follows docs/design/lura-book-v1 StepService, including its empty state.

function StepService({ slug, profile, flow, verification = false, signIn = false }: Props) {
  const { t, fmt } = useTranslation();
  usePageTitle(t("flow.service.heading"));
  const navigate = useNavigate();
  const selected = flow.selection.serviceId;
  const chosen = profile.services.find((service) => service.id === selected);

  return (
    <ServiceFrame
      testId="step-service"
      lead={<StepLeadBar backTo={profile.requiresClientAccount ? `/${slug}` : undefined} step={1} of={verification ? 5 : 4} v3 />}
    >

      <div className="b-scroll">
        {/* After the footer's Sign out, at the top and focused. Only the
            deliberate line: a session end routes to sign-in, which says its
            own. */}
        <SessionNotice only="signed-out" testId="step-service-sign-out-notice" />
        {/* WEB V2 · 05 "Choose a service": who they are, then what they do. The
            initials are decoration; the name is the text a reader hears. */}
        <div className="b2-pro" data-testid="step-service-pro">
          <span className="b2-pro-mark" aria-hidden="true">
            {initials(profile.businessName)}
          </span>
          <span className="b2-pro-text">
            <span className="b2-pro-name">{profile.businessName}</span>
            {profile.addressDisplay ? <span className="b-meta">{profile.addressDisplay}</span> : null}
          </span>
        </div>
        <h1 className="b-display" tabIndex={-1}>{t("flow.service.heading")}</h1>

        {profile.services.length === 0 ? (
          // A professional with a live booking link and no published services.
          // Says so plainly instead of rendering an empty list, and does not
          // blame the client for a page that cannot do anything.
          <div className="b-notice t-quiet" data-testid="step-service-empty">
            <div className="b-notice-b">
              <b>{t("flow.service.empty.title", { business: profile.businessName })}</b>
              {t("flow.service.empty.body") ? <p className="b-p">{t("flow.service.empty.body", { business: profile.businessName })}</p> : null}
            </div>
          </div>
        ) : (
          <>
            {/*
              A radiogroup, not a list of buttons. Arrow keys move between
              options and the group is announced with its selected value —
              which is what a screen reader user needs to know they are picking
              one of several, not activating several separate controls.
            */}
            <div className="b-services" role="radiogroup" aria-label={t("flow.service.heading")}>
              {profile.services.map((service) => {
                const isSelected = service.id === selected;
                return (
                  <button
                    key={service.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={isSelected || (!chosen && service === profile.services[0]) ? 0 : -1}
                    onKeyDown={radioKeys}
                    className={`b-card b-service${isSelected ? " is-on" : ""}`}
                    data-testid={`service-${service.id}`}
                    onClick={() => flow.chooseService(service.id)}
                  >
                    <span className="b3-radio" aria-hidden="true">{isSelected ? "✓" : ""}</span>
                    <span className="b-stack g6" style={{ flex: 1, textAlign: "left" }}>
                      <span className="b-h3">{service.name}</span>
                      <span className="b-meta">{fmt.duration(service.durationMinutes)}</span>
                      {service.description ? (
                        <span className="b-meta">{service.description}</span>
                      ) : null}
                    </span>
                    <span className="b-money">{fmt.money(service.priceInCents)}</span>
                  </button>
                );
              })}
            </div>

            {/* Product's contract: Lura charges nothing at booking time. Said
                on the step where a price first appears, not buried at review. */}
            {t("flow.service.priceNote") ? <p className="b-meta">{t("flow.service.priceNote", { business: profile.businessName })}</p> : null}
          </>
        )}
        <StepMeta
          secondary={
            signIn ? (
              <Link
                className="b-btn v-ghost is-full"
                data-testid="step-service-signin"
                to={`/${slug}/book/signin${window.location.search}`}
              >
                {t("flow.signin.heading")}
              </Link>
            ) : null
          }
        />
      </div>

      <StepFoot>
        <button
          type="button"
          className={`b-btn v-primary is-full${chosen ? "" : " is-off"}`}
          data-testid="step-service-continue"
          disabled={!chosen}
          onClick={() => {
            requestStepFocus();
            navigate(`/${slug}/book/time${window.location.search}`);
          }}
        >
          {t("flow.continue")}
        </button>
      </StepFoot>
    </ServiceFrame>
  );
}

/**
 * The service step's ONE <main>, for the step and for its loading placeholder,
 * so the two cannot drift apart in class or landmark. `loading` adds the
 * shape class (v3.css gives it the step's narrower rail, which the step gets
 * from its test id) and the `data-state` the suites read.
 */
/**
 * The service step's frame, and the two skeletons' (`shape="step"` for the
 * generic one). A FlowFrame (components/PageShell.tsx, c6): inside the flow
 * shell it publishes its chrome and the shell's header survives the arrival.
 */
export function ServiceFrame({
  testId,
  loading = false,
  shape = "service",
  lead,
  children,
}: {
  testId: string;
  loading?: boolean;
  shape?: "service" | "step";
  lead: ReactNode;
  children: ReactNode;
}) {
  return (
    <FlowFrame
      testId={testId}
      className={loading && shape === "service" ? "b3-service-shape" : undefined}
      data={{ state: loading ? "loading" : undefined }}
      lead={lead}
    >
      {children}
    </FlowFrame>
  );
}

/** The skeletons' lead: the stepbar's shapes and the bare rule. */
export function SkeletonStepLead() {
  return (
    <div aria-hidden="true" inert data-testid="skeleton-stepbar">
      <div className="b2-stepbar">
        <Skeleton w={92} h={12} />
        <Skeleton w={56} h={12} />
      </div>
      {/* The rule only: the step count is not known until the identity is. */}
      <div className="b-progress">
        {[0, 1, 2, 3].map((segment) => <i key={segment} />)}
      </div>
    </div>
  );
}

/**
 * THE SERVICE STEP, BEFORE THE PROFESSIONAL IS KNOWN.
 *
 * `/:slug` resolves straight to `/:slug/book/service`, and `/book/service`
 * itself waits on the same profile read and then on the client identity. All
 * three waits draw this, so the first thing a client sees on the QR-code path
 * is the page they are about to get: the step bar and its rule, the business
 * name and address, "Choose a service", the service rows, and the pinned
 * Continue bar (one button: Sign in sits in the scrolling footer, Design
 * 2026-10-12). The real elements' classes carry the geometry (same
 * header, scroll rail, row cards and action bar), so content landing fills the
 * blocks in rather than moving them. No v3 comp draws this state; it follows
 * the resolved screen.
 *
 * Every placeholder group is `aria-hidden` AND `inert`: the action bar in
 * particular holds no button, real or disabled, because a control that cannot
 * work is a dead control. A screen reader gets the hidden h1 and one polite
 * "Loading…", as before. Only the shared header and footer are live, and
 * both are the real ones the step renders too.
 *
 * `testId` and `data-state="loading"` sit on <main> (the step's own
 * ServiceFrame), which is what callers' suites read; `profile-skeleton` stays
 * on the body placeholders.
 */
export function ServiceStepSkeleton({ testId }: { testId: string }) {
  const { t } = useTranslation();
  return (
    <ServiceFrame testId={testId} loading lead={<SkeletonStepLead />}>

      <div className="b-scroll">
        {/* An h1 even here: a page without one is an axe violation, and the
            professional's name is not known yet, so it is visually hidden
            rather than invented. */}
        <h1 className="b-visually-hidden">{t("profile.loading")}</h1>

        <div className="b3-service-placeholders" aria-hidden="true" inert data-testid="profile-skeleton">
          <div className="b2-pro-text" data-skeleton="identity">
            <Skeleton w="56%" h={36} />
            <Skeleton w="34%" h={20} />
          </div>
          <Skeleton w="52%" h={27} />
          {/* Two rows, not a guess at the real count: with three, a phone or a
              1280x800 window overflows the scroll area and the legal footer
              starts lower than the step will put it. */}
          <div className="b-services" data-skeleton="services">
            {[0, 1].map((row) => (
              <div key={row} className="b-card b-service">
                <Skeleton w={20} h={20} r={999} />
                <span className="b-stack g6" style={{ flex: 1 }}>
                  <Skeleton w="46%" h={21} />
                  <Skeleton w="22%" h={20} />
                </span>
                <Skeleton w={40} h={21} />
              </div>
            ))}
          </div>
        </div>

        <p className="b-visually-hidden" aria-live="polite">
          {t("profile.loading")}
        </p>
        {/* StepMeta without the consent banner: the banner is the step's own,
            and mounting it here would hand it focus duties twice. */}
        <div className="b-stepmeta">
          <Footer as="div" />
        </div>
      </div>

      <div className="b-foot" aria-hidden="true" inert data-testid="skeleton-actions">
        <Skeleton h={50} r={10} />
      </div>
    </ServiceFrame>
  );
}

/** Up to two initials, for the identity mark only. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export default StepService;
